import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "./ProductCard";

export default function CategoryRow({ category, emoji = "" }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const scroller = useRef(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    useEffect(() => {
        api.get("/products", { params: { category } })
            .then(({ data }) => setProducts(data))
            .finally(() => setLoading(false));
    }, [category]);

    const updateButtons = () => {
        const el = scroller.current;
        if (!el) return;
        setCanLeft(el.scrollLeft > 4);
        setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };

    useEffect(() => {
        updateButtons();
        const el = scroller.current;
        if (!el) return;
        el.addEventListener("scroll", updateButtons, { passive: true });
        window.addEventListener("resize", updateButtons);
        return () => {
            el.removeEventListener("scroll", updateButtons);
            window.removeEventListener("resize", updateButtons);
        };
    }, [products]);

    const scrollBy = (dir) => {
        const el = scroller.current;
        if (!el) return;
        const amount = Math.max(280, el.clientWidth * 0.8);
        el.scrollBy({ left: dir * amount, behavior: "smooth" });
    };

    if (!loading && products.length === 0) return null;

    return (
        <section
            data-testid={`category-row-${category}`}
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
        >
            <div className="flex items-end justify-between mb-3">
                <div>
                    <h3 className="font-display text-2xl sm:text-3xl font-semibold text-cocoa flex items-center gap-2">
                        {emoji && <span className="text-2xl">{emoji}</span>}
                        {category}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                        {products.length} producto{products.length !== 1 ? "s" : ""}
                    </p>
                </div>
                <Link
                    to={`/productos?categoria=${encodeURIComponent(category)}`}
                    className="text-flagred font-semibold text-sm hover:underline"
                >
                    Ver todo →
                </Link>
            </div>

            <div className="relative group">
                <button
                    aria-label={`Anterior ${category}`}
                    data-testid={`row-prev-${category}`}
                    onClick={() => scrollBy(-1)}
                    disabled={!canLeft}
                    className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-border items-center justify-center transition-all ${
                        canLeft ? "opacity-100 hover:bg-mexyellow hover:scale-110" : "opacity-0 pointer-events-none"
                    }`}
                >
                    <ChevronLeft className="w-5 h-5 text-cocoa" />
                </button>
                <button
                    aria-label={`Siguiente ${category}`}
                    data-testid={`row-next-${category}`}
                    onClick={() => scrollBy(1)}
                    disabled={!canRight}
                    className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-border items-center justify-center transition-all ${
                        canRight ? "opacity-100 hover:bg-mexyellow hover:scale-110" : "opacity-0 pointer-events-none"
                    }`}
                >
                    <ChevronRight className="w-5 h-5 text-cocoa" />
                </button>

                <div
                    ref={scroller}
                    className="flex gap-3 sm:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-3 -mx-1 px-4 sm:px-1 scrollbar-thin"
                    style={{ scrollbarWidth: "thin" }}
                >
                    {loading
                        ? Array.from({ length: 4 }).map((_, i) => (
                              <div
                                  key={i}
                                  className="w-[calc(50%-0.5rem)] xs:w-40 sm:w-56 shrink-0 aspect-[3/4] rounded-3xl bg-muted/40 animate-pulse"
                              />
                          ))
                        : products.map((p) => (
                              <div
                                  key={p.id}
                                  className="w-[calc(50%-0.5rem)] xs:w-40 sm:w-56 shrink-0 snap-start"
                              >
                                  <ProductCard product={p} />
                              </div>
                          ))}
                    {/* Spacer so the last card isn't glued to the edge on mobile */}
                    <div className="w-2 shrink-0 sm:hidden" />
                </div>
            </div>
        </section>
    );
}
